import { CommonModule, DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbTooltipMfeModule } from '@client/mfe/overlay/ngb-tooltip-mfe.module';
import { UIRouterModule } from '@uirouter/angular';
import { StateService, Transition, TransitionService } from '@uirouter/core';
import { TranslocoService } from '@jsverse/transloco';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { DomainPickerComponent } from '@client/app/feature-modules/navigation/components/domain-picker/domain-picker.component';
import {
    SideNavigationItem,
    SideNavigationProfile,
} from './side-navigation.menu-config';
import { SvgIconComponent } from '@client/app/shared-modules/svg-icons/components/svg-icon/svg-icon.component';
import { SvgIconName } from '@client/app/shared-modules/svg-icons/services/svg-icon.service';
import { IS_MICROFRONTEND, SHADOW_ROOT_TOKEN } from '@client/mfe/tokens/mfe-tokens';

interface CollapsedPopupPosition {
    top: number;
    left: number;
    maxHeight: number;
}

interface CollapsedPopupTriggerRef {
    type: 'root' | 'popup-disclosure';
    itemId: string;
    popupLevel: number | null;
}

interface CollapsedPopupLevel {
    panelId: string;
    parentItemId: string;
    title: string;
    items: SideNavigationItem[];
    position: CollapsedPopupPosition;
    labelledById: string;
    triggerRef: CollapsedPopupTriggerRef;
}

/**
 * Viewport widths below this value never reserve expanded side-nav width in
 * the page layout (see base.component.css), so the nav must stay collapsed
 * there to avoid overlapping the page body.
 */
const SIDE_NAV_EXPANDED_MIN_WIDTH = 1440;

const SIDE_NAV_SVG_ICON_NAMES: ReadonlySet<SvgIconName> = new Set([
    'dashboard',
    'prioritizedPages',
    'accessibility',
    'contentQuality',
    'insights',
    'sitePerformance',
    'privacy',
    'home',
    'group',
    'person',
    'policy',
    'build',
    'label',
    'speed',
    'block',
    'extension',
    'token',
    'link',
]);

@Component({
    selector: 'mon-side-navigation',
    standalone: true,
    imports: [
        CommonModule,
        NgbTooltipMfeModule,
        UIRouterModule,
        DomainPickerComponent,
        SvgIconComponent,
    ],
    templateUrl: './side-navigation.component.html',
    styleUrl: './side-navigation.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideNavigationComponent {
    private readonly document = inject(DOCUMENT);
    private readonly hostElement = inject(ElementRef) as ElementRef<HTMLElement>;
    private readonly destroyRef = inject(DestroyRef);
    private readonly stateService = inject(StateService);
    private readonly transitionService = inject(TransitionService);
    private readonly monEventService = inject(MonEventService);
    private readonly translocoService = inject(TranslocoService);
    private readonly langChanges = toSignal(this.translocoService.langChanges$);
    private readonly shadowRoot = inject(SHADOW_ROOT_TOKEN, { optional: true });
    private readonly isMicrofrontend = inject(IS_MICROFRONTEND, { optional: true });

    readonly menuItemsInput = input<SideNavigationItem[]>([]);
    readonly activeProfileInput = input<SideNavigationProfile>('domains');

    protected readonly isCollapsed = signal(typeof window !== 'undefined' && window.innerWidth < SIDE_NAV_EXPANDED_MIN_WIDTH);
    private readonly expandedDisclosureIds = signal<Set<string>>(new Set());
    private readonly activeItemId = signal<string | null>(null);
    private readonly activePathIds = signal<Set<string>>(new Set());

    private readonly collapsedPopupLevelsState = signal<CollapsedPopupLevel[]>([]);

    protected readonly menuItems = computed((): SideNavigationItem[] => {
        // Depend on langChanges to re-compute when language loads/changes
        this.langChanges();
        return this.translateMenuItems(this.menuItemsInput());
    });
    protected readonly activeProfile = computed((): SideNavigationProfile => this.activeProfileInput());
    protected readonly expandedIds = this.expandedDisclosureIds.asReadonly();
    protected readonly activeId = this.activeItemId.asReadonly();
    protected readonly activePath = this.activePathIds.asReadonly();
    protected readonly collapseButtonText = computed((): string => {
        // Depend on langChanges to re-compute when language loads/changes
        this.langChanges();
        return this.translocoService.translate(this.isCollapsed() ? 'navigation.sideNavigation.expand' : 'navigation.sideNavigation.collapse');
    });
    protected readonly collapsedPopupLevels = this.collapsedPopupLevelsState.asReadonly();

    private itemById = new Map<string, SideNavigationItem>();
    private expandedDisclosureOrder: string[] = [];

    private readonly widthSyncEffect = effect((): void => {
        this.syncWidthVariable();
    });

    private readonly menuItemsSyncEffect = effect((): void => {
        const menuItems = this.menuItemsInput();
        this.itemById = this.createItemIndex(menuItems);
        this.syncStateFromRoute(this.stateService.current?.name, this.stateService.params);
        this.ensureCollapsedPopupStateForMenuChange();
    });

    private translateMenuItems (items: SideNavigationItem[]): SideNavigationItem[] {
        return items.map((item) => ({
            ...item,
            label: this.translocoService.translate(item.label),
            ...(item.children ? { children: this.translateMenuItems(item.children) } : {}),
        }));
    }

    constructor () {
        this.setupRouteTracking();
        this.setupViewportWidthSync();
        this.setupDocumentInteractionTracking();
    }

    onDisclosureToggle (disclosureId: string): void {
        if (this.expandedDisclosureIds().has(disclosureId)) {
            this.collapseDisclosure(disclosureId);
            return;
        }

        this.expandDisclosure(disclosureId);
    }

    onCollapsedGroupToggle (itemId: string, event?: Event): void {
        this.dismissTooltip(event);
        if (!this.isCollapsed()) {
            return;
        }

        if (this.isCollapsedPopupOpen(itemId)) {
            this.closeCollapsedPopupStack(false);
            return;
        }

        this.openCollapsedRootPopup(itemId);
    }

    onCollapsedPopupDisclosureToggle (popupLevelIndex: number, disclosureId: string): void {
        if (!this.isCollapsed()) {
            return;
        }

        if (this.isCollapsedPopupDisclosureOpen(popupLevelIndex, disclosureId)) {
            this.closeCollapsedPopupLevel(popupLevelIndex + 1, false);
            return;
        }

        this.openCollapsedNestedPopup(popupLevelIndex, disclosureId);
    }

    onCollapsedLeafNavigate (event?: Event): void {
        this.dismissTooltip(event);
        this.closeCollapsedPopupStack(false);
    }

    toggleCollapsedState (): void {
        const nextCollapsedState = !this.isCollapsed();
        this.isCollapsed.set(nextCollapsedState);
        this.closeCollapsedPopupStack(false);

        if (nextCollapsedState) {
            this.collapseAllDisclosures();
            return;
        }

        this.syncStateFromRoute(this.stateService.current?.name, this.stateService.params);
    }

    onNavigationKeydown (event: KeyboardEvent): void {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const actionElement = target.closest('.side-nav__action');
        if (!(actionElement instanceof HTMLElement)) {
            return;
        }

        const actionType = actionElement.dataset['actionType'];
        const itemId = actionElement.dataset['itemId'];

        switch (event.key) {
            case 'Escape':
                event.preventDefault();
                this.handleEscapeKey();
                return;
            case 'ArrowDown':
            case 'ArrowRight':
                event.preventDefault();
                if (this.isCollapsed()) {
                    this.handleCollapsedForwardNavigation(actionElement);
                    return;
                }
                this.focusNextAction(actionElement, itemId, actionType);
                return;
            case 'ArrowUp':
            case 'ArrowLeft':
                event.preventDefault();
                if (this.isCollapsed()) {
                    this.focusSiblingAction(actionElement, -1, false);
                    return;
                }
                this.focusSiblingAction(actionElement, -1);
                return;
            case 'Home':
                event.preventDefault();
                this.focusBoundarySibling(actionElement, true);
                return;
            case 'End':
                event.preventDefault();
                this.focusBoundarySibling(actionElement, false);
                return;
            default:
                return;
        }
    }

    resolveItemRoute (item: SideNavigationItem): string {
        return item.route || '';
    }

    resolveItemParams (item: SideNavigationItem): Record<string, unknown> {
        return item.routeParams || {};
    }

    hasChildren (item: SideNavigationItem): boolean {
        return Boolean(item.children && item.children.length > 0);
    }

    isDialogItem (item: SideNavigationItem): boolean {
        return Boolean(item.dialogParams && !item.route);
    }

    onDialogItemClick (item: SideNavigationItem, event?: Event): void {
        this.dismissTooltip(event);
        if (!item.dialogParams) {
            return;
        }

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, {
            params: item.dialogParams,
        });

        this.closeCollapsedPopupStack(false);
    }

    resolveNavigationIcon (icon?: string): SvgIconName | null {
        if (!icon || !SIDE_NAV_SVG_ICON_NAMES.has(icon as SvgIconName)) {
            return null;
        }

        return icon as SvgIconName;
    }

    isCollapsedPopupOpen (itemId: string): boolean {
        const popupLevels = this.collapsedPopupLevelsState();
        const rootPopupLevel = popupLevels[0];
        return Boolean(rootPopupLevel && rootPopupLevel.parentItemId === itemId);
    }

    isCollapsedPopupDisclosureOpen (popupLevelIndex: number, itemId: string): boolean {
        const popupLevels = this.collapsedPopupLevelsState();
        const childPopupLevel = popupLevels[popupLevelIndex + 1];
        return Boolean(childPopupLevel && childPopupLevel.parentItemId === itemId);
    }

    getCollapsedPopupPanelId (popupLevelIndex: number, itemId: string): string {
        return `side-nav-collapsed-popup-panel-${popupLevelIndex}-${itemId}`;
    }

    getCollapsedPopupDisclosureId (popupLevelIndex: number, itemId: string): string {
        return `side-nav-collapsed-disclosure-${popupLevelIndex}-${itemId}`;
    }

    private syncWidthVariable (): void {
        const root: HTMLElement = (this.isMicrofrontend && this.shadowRoot)
            ? (this.shadowRoot.querySelector('mfe-root') as HTMLElement ?? this.shadowRoot.host as HTMLElement)
            : this.document.documentElement;

        if (window.innerWidth <= 768) {
            root.style.setProperty('--current-side-nav-width', '0px');
            return;
        }
        const widthValue = this.isCollapsed()
            ? 'var(--p-side-nav-collapsed-width)'
            : 'var(--p-side-nav-expanded-width)';
        root.style.setProperty('--current-side-nav-width', widthValue);
    }

    private setupViewportWidthSync (): void {
        const observeTarget: Element = (this.isMicrofrontend && this.shadowRoot)
            ? this.shadowRoot.host
            : this.document.documentElement;

        const observer = new ResizeObserver((): void => {
            this.syncWidthVariable();
            this.collapseBelowThreshold();
        });
        observer.observe(observeTarget);
        this.destroyRef.onDestroy((): void => {
            observer.disconnect();
        });
    }

    private collapseBelowThreshold (): void {
        if (window.innerWidth >= SIDE_NAV_EXPANDED_MIN_WIDTH || this.isCollapsed()) {
            return;
        }

        this.isCollapsed.set(true);
        this.collapseAllDisclosures();
        this.closeCollapsedPopupStack(false);
    }

    private setupDocumentInteractionTracking (): void {
        const handleDocumentPointerDown = (event: MouseEvent): void => {
            this.handleDocumentPointerDown(event);
        };

        const handleDocumentFocusIn = (event: FocusEvent): void => {
            this.handleDocumentFocusIn(event);
        };

        this.document.addEventListener('mousedown', handleDocumentPointerDown);
        this.document.addEventListener('focusin', handleDocumentFocusIn);

        this.destroyRef.onDestroy((): void => {
            this.document.removeEventListener('mousedown', handleDocumentPointerDown);
            this.document.removeEventListener('focusin', handleDocumentFocusIn);
        });
    }

    private closeMobileMenu (): void {
        if (window.innerWidth <= 768) {
            this.isCollapsed.set(true);
        }

        this.closeCollapsedPopupStack(false);
    }

    private focusNextAction (actionElement: HTMLElement, itemId?: string, actionType?: string): void {
        if (actionType === 'disclosure' && itemId && this.expandedDisclosureIds().has(itemId)) {
            if (this.focusFirstActionInPanel(`side-nav-panel-${itemId}`)) {
                return;
            }
        }

        this.focusSiblingAction(actionElement, 1);
    }

    private handleCollapsedForwardNavigation (actionElement: HTMLElement): void {
        if (this.focusFirstActionInExpandedCollapsedPanel(actionElement)) {
            return;
        }

        this.focusSiblingAction(actionElement, 1, false);
    }

    private focusFirstActionInExpandedCollapsedPanel (actionElement: HTMLElement): boolean {
        if (actionElement.getAttribute('aria-expanded') !== 'true') {
            return false;
        }

        const controlledPanelId = actionElement.getAttribute('aria-controls');
        if (!controlledPanelId) {
            return false;
        }

        return this.focusFirstActionInPanel(controlledPanelId);
    }

    private setupRouteTracking (): void {
        const deregisterHook: unknown = this.transitionService.onSuccess({}, (transition: Transition) => {
            const nextStateName = transition.to().name;
            this.syncStateFromRoute(nextStateName, this.stateService.params);
            this.closeMobileMenu();
        });

        this.destroyRef.onDestroy(() => {
            if (typeof deregisterHook === 'function') {
                (deregisterHook as () => void)();
            }
        });
    }

    private handleDocumentPointerDown (event: MouseEvent): void {
        if (this.collapsedPopupLevelsState().length === 0 || !this.isCollapsed()) {
            return;
        }

        const target = (event.composedPath()[0] ?? event.target) as Node;
        if (!(target instanceof Node)) {
            return;
        }

        if (this.isNodeInsideCollapsedPopup(target) || this.isNodeInsideCollapsedTrigger(target)) {
            return;
        }

        this.closeCollapsedPopupStack(false);
    }

    private handleDocumentFocusIn (event: FocusEvent): void {
        if (this.collapsedPopupLevelsState().length === 0 || !this.isCollapsed()) {
            return;
        }

        const target = (event.composedPath()[0] ?? event.target) as Node;
        if (!(target instanceof Node)) {
            return;
        }

        if (this.isNodeInsideCollapsedPopup(target) || this.isNodeInsideCollapsedTrigger(target)) {
            return;
        }

        this.closeCollapsedPopupStack(false);
    }

    private isNodeInsideCollapsedPopup (target: Node): boolean {
        const popupElements = this.hostElement.nativeElement.querySelectorAll<HTMLElement>('.side-navigation__collapsed-popup');

        for (const popupElement of Array.from(popupElements)) {
            if (popupElement.contains(target)) {
                return true;
            }
        }

        return false;
    }

    private isNodeInsideCollapsedTrigger (target: Node): boolean {
        if (!(target instanceof Element)) {
            return false;
        }

        return Boolean(target.closest('[data-collapsed-trigger-id]'));
    }

    private syncStateFromRoute (stateName?: string, currentParams: Record<string, unknown> = {}): void {
        if (!stateName) {
            this.clearActiveState();
            return;
        }

        const path = this.findPathByRoute(this.menuItems(), stateName.toLowerCase(), currentParams);

        if (!path) {
            this.clearActiveState();
            return;
        }

        this.setActivePath(path);

        const disclosureIdsInPath = path
            .filter((item) => item.children && item.children.length > 0)
            .map((item) => item.id);

        this.expandedDisclosureIds.set(new Set(disclosureIdsInPath));
        this.expandedDisclosureOrder = [...disclosureIdsInPath];
    }

    private clearActiveState (): void {
        this.activeItemId.set(null);
        this.activePathIds.set(new Set());
        this.collapseAllDisclosures();
        this.closeCollapsedPopupStack(false);
    }

    private setActivePath (path: SideNavigationItem[]): void {
        const activeItem = path[path.length - 1];
        this.activeItemId.set(activeItem ? activeItem.id : null);
        this.activePathIds.set(new Set(path.map((item) => item.id)));
    }

    private expandDisclosure (disclosureId: string): void {
        const item = this.itemById.get(disclosureId);
        if (!item || !item.children || item.children.length === 0) {
            return;
        }

        this.expandedDisclosureIds.update((currentExpandedIds) => {
            const nextExpandedIds = new Set(currentExpandedIds);
            nextExpandedIds.add(disclosureId);
            return nextExpandedIds;
        });

        if (!this.expandedDisclosureOrder.includes(disclosureId)) {
            this.expandedDisclosureOrder.push(disclosureId);
        }
    }

    private collapseDisclosure (disclosureId: string): void {
        const item = this.itemById.get(disclosureId);
        if (!item) {
            return;
        }

        const descendantDisclosureIds = this.collectDescendantDisclosureIds(item);
        const idsToClose = new Set([disclosureId, ...descendantDisclosureIds]);

        this.expandedDisclosureIds.update((currentExpandedIds) => {
            const nextExpandedIds = new Set(currentExpandedIds);
            for (const id of idsToClose) {
                nextExpandedIds.delete(id);
            }
            return nextExpandedIds;
        });

        this.expandedDisclosureOrder = this.expandedDisclosureOrder.filter((id) => !idsToClose.has(id));
    }

    private collapseAllDisclosures (): void {
        this.expandedDisclosureIds.set(new Set());
        this.expandedDisclosureOrder = [];
    }

    private handleEscapeKey (): void {
        if (this.isCollapsed() && this.collapsedPopupLevelsState().length > 0) {
            this.handleCollapsedPopupEscapeKey();
            return;
        }

        this.handleExpandedDisclosureEscapeKey();
    }

    private handleExpandedDisclosureEscapeKey (): void {
        if (this.expandedDisclosureOrder.length === 0) {
            return;
        }

        const disclosureId = this.expandedDisclosureOrder[this.expandedDisclosureOrder.length - 1];
        if (!disclosureId) {
            return;
        }

        this.collapseDisclosure(disclosureId);
        this.focusDisclosureButton(disclosureId);
    }

    private handleCollapsedPopupEscapeKey (): void {
        const popupLevels = this.collapsedPopupLevelsState();
        if (popupLevels.length === 0) {
            return;
        }

        this.closeCollapsedPopupLevel(popupLevels.length - 1, true);
    }

    private openCollapsedRootPopup (rootItemId: string): void {
        const rootItem = this.itemById.get(rootItemId);
        if (!rootItem || !rootItem.children || rootItem.children.length === 0) {
            return;
        }

        const triggerElement = this.getCollapsedTriggerElement(rootItemId);
        if (!(triggerElement instanceof HTMLElement)) {
            return;
        }

        this.openCollapsedPopupLevel(
            0,
            rootItem,
            triggerElement,
            {
                type: 'root',
                itemId: rootItemId,
                popupLevel: null,
            },
        );
    }

    private openCollapsedNestedPopup (popupLevelIndex: number, parentItemId: string): void {
        const parentItem = this.itemById.get(parentItemId);
        if (!parentItem || !parentItem.children || parentItem.children.length === 0) {
            return;
        }

        const triggerElement = this.getCollapsedPopupDisclosureTriggerElement(popupLevelIndex, parentItemId);
        if (!(triggerElement instanceof HTMLElement)) {
            return;
        }

        this.openCollapsedPopupLevel(
            popupLevelIndex + 1,
            parentItem,
            triggerElement,
            {
                type: 'popup-disclosure',
                itemId: parentItemId,
                popupLevel: popupLevelIndex,
            },
        );
    }

    private openCollapsedPopupLevel (
        popupLevelIndex: number,
        parentItem: SideNavigationItem,
        triggerElement: HTMLElement,
        triggerRef: CollapsedPopupTriggerRef,
    ): void {
        if (!parentItem.children || parentItem.children.length === 0) {
            return;
        }

        const labelledById = triggerRef.type === 'root'
            ? `side-nav-collapsed-trigger-${triggerRef.itemId}`
            : this.getCollapsedPopupDisclosureId(triggerRef.popupLevel ?? 0, triggerRef.itemId);

        const nextPopupLevel: CollapsedPopupLevel = {
            panelId: this.getCollapsedPopupPanelId(popupLevelIndex, parentItem.id),
            parentItemId: parentItem.id,
            title: this.translocoService.translate(parentItem.label),
            items: this.translateMenuItems(parentItem.children),
            position: this.calculateCollapsedPopupPosition(triggerElement),
            labelledById,
            triggerRef,
        };

        this.collapsedPopupLevelsState.update((currentPopupLevels) => {
            const popupLevelsToKeep = currentPopupLevels.slice(0, popupLevelIndex);
            return [...popupLevelsToKeep, nextPopupLevel];
        });
    }

    private closeCollapsedPopupLevel (popupLevelIndex: number, restoreFocus: boolean): void {
        const popupLevels = this.collapsedPopupLevelsState();
        if (popupLevelIndex < 0 || popupLevelIndex >= popupLevels.length) {
            return;
        }

        const popupLevelToClose = popupLevels[popupLevelIndex];
        this.collapsedPopupLevelsState.set(popupLevels.slice(0, popupLevelIndex));

        if (restoreFocus && popupLevelToClose) {
            this.focusCollapsedPopupTrigger(popupLevelToClose.triggerRef);
        }
    }

    private closeCollapsedPopupStack (restoreFocus: boolean): void {
        const popupLevels = this.collapsedPopupLevelsState();
        if (popupLevels.length === 0) {
            return;
        }

        this.closeCollapsedPopupLevel(0, restoreFocus);
    }

    private ensureCollapsedPopupStateForMenuChange (): void {
        const popupLevels = this.collapsedPopupLevelsState();
        if (popupLevels.length === 0) {
            return;
        }

        for (const popupLevel of popupLevels) {
            const item = this.itemById.get(popupLevel.parentItemId);
            if (!item || !item.children || item.children.length === 0) {
                this.closeCollapsedPopupStack(false);
                return;
            }
        }
    }

    private calculateCollapsedPopupPosition (triggerElement: HTMLElement): CollapsedPopupPosition {
        const triggerRect = triggerElement.getBoundingClientRect();
        const viewportPadding = 8;
        const horizontalGap = 8;
        const estimatedPopupWidth = 320;

        const hostRect = (this.isMicrofrontend && this.shadowRoot)
            ? (this.shadowRoot.host as HTMLElement).getBoundingClientRect()
            : { top: 0, left: 0 };

        let left = triggerRect.right + horizontalGap;
        const rightBoundary = window.innerWidth - viewportPadding;

        if (left + estimatedPopupWidth > rightBoundary) {
            left = Math.max(viewportPadding, triggerRect.left - estimatedPopupWidth - horizontalGap);
        }

        const minimumTop = viewportPadding;
        const maximumTop = window.innerHeight - viewportPadding - 160;
        const top = Math.max(minimumTop, Math.min(triggerRect.top, maximumTop));

        return {
            top: top - hostRect.top,
            left: left - hostRect.left,
            maxHeight: Math.max(160, window.innerHeight - top - viewportPadding),
        };
    }

    private focusDisclosureButton (disclosureId: string): void {
        const disclosureButton = this.hostElement.nativeElement.querySelector<HTMLElement>(
            `[data-disclosure-id="${disclosureId}"]`,
        );

        if (disclosureButton instanceof HTMLElement) {
            disclosureButton.focus();
        }
    }

    private focusCollapsedPopupDisclosureButton (popupLevelIndex: number, disclosureId: string): void {
        const disclosureButton = this.hostElement.nativeElement.querySelector<HTMLElement>(
            `[data-collapsed-popup-disclosure-id="${disclosureId}"][data-popup-level="${popupLevelIndex}"]`,
        );

        if (disclosureButton instanceof HTMLElement) {
            disclosureButton.focus();
        }
    }

    private focusCollapsedTriggerButton (itemId: string): void {
        const triggerButton = this.getCollapsedTriggerElement(itemId);

        if (triggerButton instanceof HTMLElement) {
            triggerButton.focus();
        }
    }

    private focusCollapsedPopupTrigger (triggerRef: CollapsedPopupTriggerRef): void {
        if (triggerRef.type === 'root') {
            this.focusCollapsedTriggerButton(triggerRef.itemId);
            return;
        }

        const popupLevel = triggerRef.popupLevel;
        if (popupLevel === null) {
            return;
        }

        this.focusCollapsedPopupDisclosureButton(popupLevel, triggerRef.itemId);
    }

    private focusFirstActionInPanel (panelId: string): boolean {
        const panelElement = this.hostElement.nativeElement.querySelector<HTMLElement>(`#${panelId}`);
        if (!(panelElement instanceof HTMLElement)) {
            return false;
        }

        const directListItemAction = panelElement.querySelector(':scope > li > .side-nav__action');
        if (directListItemAction instanceof HTMLElement) {
            directListItemAction.focus();
            return true;
        }

        const directListElement = panelElement.querySelector(':scope > ul');
        if (!(directListElement instanceof HTMLElement)) {
            return false;
        }

        const nestedListItemAction = directListElement.querySelector(':scope > li > .side-nav__action');
        if (!(nestedListItemAction instanceof HTMLElement)) {
            return false;
        }

        nestedListItemAction.focus();
        return true;
    }

    private focusSiblingAction (actionElement: HTMLElement, offset: number, shouldWrap = true): void {
        const siblingActions = this.getSiblingActions(actionElement);
        if (siblingActions.length === 0) {
            return;
        }

        const currentIndex = siblingActions.indexOf(actionElement);
        if (currentIndex === -1) {
            return;
        }

        const targetIndex = shouldWrap
            ? (currentIndex + offset + siblingActions.length) % siblingActions.length
            : currentIndex + offset;

        if (targetIndex < 0 || targetIndex >= siblingActions.length) {
            return;
        }

        const targetAction = siblingActions[targetIndex];
        if (targetAction) {
            targetAction.focus();
        }
    }

    private focusBoundarySibling (actionElement: HTMLElement, focusFirst: boolean): void {
        const siblingActions = this.getSiblingActions(actionElement);
        if (siblingActions.length === 0) {
            return;
        }

        const targetAction = focusFirst
            ? siblingActions[0]
            : siblingActions[siblingActions.length - 1];

        if (targetAction) {
            targetAction.focus();
        }
    }

    private getSiblingActions (actionElement: HTMLElement): HTMLElement[] {
        const listItem = actionElement.closest('li');
        if (!(listItem instanceof HTMLElement)) {
            return [];
        }

        const parentList = listItem.parentElement;
        if (!(parentList instanceof HTMLElement)) {
            return [];
        }

        const siblingActions: HTMLElement[] = [];

        for (const child of Array.from(parentList.children)) {
            if (!(child instanceof HTMLElement)) {
                continue;
            }

            const siblingAction = child.querySelector(':scope > .side-nav__action');
            if (siblingAction instanceof HTMLElement) {
                siblingActions.push(siblingAction);
            }
        }

        return siblingActions;
    }

    private getCollapsedTriggerElement (itemId: string): HTMLElement | null {
        return this.hostElement.nativeElement.querySelector<HTMLElement>(`[data-collapsed-trigger-id="${itemId}"]`);
    }

    private getCollapsedPopupDisclosureTriggerElement (popupLevelIndex: number, itemId: string): HTMLElement | null {
        return this.hostElement.nativeElement.querySelector<HTMLElement>(
            `[data-collapsed-popup-disclosure-id="${itemId}"][data-popup-level="${popupLevelIndex}"]`,
        );
    }

    private collectDescendantDisclosureIds (item: SideNavigationItem): string[] {
        if (!item.children || item.children.length === 0) {
            return [];
        }

        const descendantDisclosureIds: string[] = [];

        for (const child of item.children) {
            if (child.children && child.children.length > 0) {
                descendantDisclosureIds.push(child.id);
                descendantDisclosureIds.push(...this.collectDescendantDisclosureIds(child));
            }
        }

        return descendantDisclosureIds;
    }

    private findPathByRoute (
        items: SideNavigationItem[],
        stateName: string,
        currentParams: Record<string, unknown>,
    ): SideNavigationItem[] | null {
        for (const item of items) {
            if (item.children && item.children.length > 0) {
                const pathFromChild = this.findPathByRoute(item.children, stateName, currentParams);
                if (pathFromChild) {
                    return [item, ...pathFromChild];
                }
            }

            if (this.itemMatchesState(item, stateName, currentParams)) {
                return [item];
            }
        }

        return null;
    }

    private itemMatchesState (
        item: SideNavigationItem,
        stateName: string,
        currentParams: Record<string, unknown>,
    ): boolean {
        const normalizedRoute = item.route?.toLowerCase();
        if (normalizedRoute && (stateName === normalizedRoute || stateName.startsWith(normalizedRoute + '.'))) {
            if (this.routeParamsMatch(item.routeParams, currentParams)) {
                return true;
            }
        }

        const activeRoutes = (item.activeRoutes || [])
            .filter((route): route is string => typeof route === 'string' && route.length > 0)
            .map((route) => route.toLowerCase());

        for (const route of activeRoutes) {
            if (stateName !== route && !stateName.startsWith(route + '.')) {
                continue;
            }

            if (normalizedRoute && route === normalizedRoute && !this.routeParamsMatch(item.routeParams, currentParams)) {
                continue;
            }

            return true;
        }

        if (item.routeMatchers && item.routeMatchers.length > 0) {
            return item.routeMatchers.some((matcher) => stateName.includes(matcher.toLowerCase()));
        }

        return false;
    }

    private routeParamsMatch (
        routeParams: Record<string, unknown> | undefined,
        currentParams: Record<string, unknown>,
    ): boolean {
        if (!routeParams) {
            return true;
        }

        const entries = Object.entries(routeParams);
        if (entries.length === 0) {
            return true;
        }

        return entries.every(([key, expectedValue]) => {
            if (expectedValue === null || expectedValue === undefined) {
                return true;
            }

            const actualValue = currentParams[key];
            if (actualValue === null || actualValue === undefined) {
                return false;
            }

            return String(actualValue) === String(expectedValue);
        });
    }

    private createItemIndex (items: SideNavigationItem[]): Map<string, SideNavigationItem> {
        const index = new Map<string, SideNavigationItem>();

        const walk = (nodes: SideNavigationItem[]): void => {
            for (const node of nodes) {
                index.set(node.id, node);

                if (node.children && node.children.length > 0) {
                    walk(node.children);
                }
            }
        };

        walk(items);
        return index;
    }

    private dismissTooltip (event?: Event): void {
        if (event?.currentTarget instanceof HTMLElement) {
            event.currentTarget.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
        }
    }

}
